import { useEffect, useState } from 'react';
import type { DashboardDto, RoleLevel } from '@genoma/shared';

import { Link } from 'react-router-dom';

import { Brand } from '../../components/Brand.tsx';
import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { fetchDashboard } from '../../services/dashboard.service.ts';
import { AlertList } from './AlertList.tsx';
import { ParticipationDonut } from './ParticipationDonut.tsx';
import { ParticipantTable } from './ParticipantTable.tsx';
import { StatIcons, StatTile } from './StatTile.tsx';

/**
 * The P&O view of a cycle.
 *
 * It answers one question — will this cycle produce usable reports — and it
 * answers it without a single rating. Everything on screen is a count of
 * people or of days, which is also why there are no charts: headline numbers,
 * one ratio per segment, and a table of nineteen rows.
 */
export const DashboardPage = () => {
  const { t } = useI18n();
  const [dashboard, setDashboard] = useState<DashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard()
      .then(setDashboard)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // Loading once on mount is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <Centered>{error}</Centered>;
  if (!dashboard) return <Centered>{t('common.loading')}</Centered>;

  const {
    brand,
    cycle,
    totals,
    participants,
    submittedByRelationship,
    // Comes from the API rather than summed from `participants`: that list is
    // only the few most recently active, so summing it undercounts the cycle.
    invitedByRelationship,
    alerts,
  } = dashboard;

  const ROLE_LEVELS: RoleLevel[] = ['AREA_LEADER', 'PEOPLE_LEADER', 'SELF_LEADER'];

  /**
   * The deadline, in whichever direction it points.
   *
   * The count is days remaining, so it goes negative once the date passes —
   * and "cierra en −6 días" is not a sentence. A closed cycle says nothing at
   * all: the date has already done its work, and a countdown next to a cycle
   * nobody can answer only invites the question of which one is lying.
   */
  const deadline = (():
    | string
    | null => {
    if (cycle.daysUntilClose === null || cycle.status !== 'OPEN') return null;
    if (cycle.daysUntilClose > 0) return t('dashboard.closesIn', { days: cycle.daysUntilClose });
    if (cycle.daysUntilClose === 0) return t('dashboard.closeDueToday');
    return t('dashboard.closeOverdue', { days: -cycle.daysUntilClose });
  })();

  return (
    <div className="mx-auto flex min-h-full max-w-6xl flex-col px-6 py-8">
      <header>
        <div>
          <Brand organization={brand.organization} program={brand.program} />
          <h1 className="mt-3 text-[28px] leading-tight font-extrabold tracking-[-0.025em]">
            {cycle.name}
          </h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
            {t('dashboard.subtitle')}
            {deadline && ` · ${deadline}`}
          </p>
        </div>

      </header>

      <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          icon={StatIcons.people}
          label={t('dashboard.evaluated')}
          value={totals.participants}
          // Most senior level first, as in the mockup — not whichever order
          // the query happened to return.
          detail={ROLE_LEVELS.filter((level) => totals.participantsByRoleLevel[level])
            .map((level) => `${totals.participantsByRoleLevel[level]} ${t(`roleLevels.${level}`)}`)
            .join(' · ')}
        />
        <StatTile
          icon={StatIcons.envelope}
          label={t('dashboard.invitedRaters')}
          value={totals.ratersInvited}
          detail={t('dashboard.invitedRatersHelp', { pairs: totals.invitedRaters })}
        />
        <StatTile
          accent
          icon={StatIcons.gauge}
          label={t('dashboard.responseRate')}
          value={`${totals.participationRate}%`}
          detail={t('dashboard.completedOf', {
            completed: totals.ratersCompleted,
            invited: totals.ratersInvited,
          })}
        />
        <StatTile
          icon={StatIcons.approved}
          label={t('dashboard.reportsApproved')}
          value={`${totals.reportsApproved}/${totals.participants}`}
          detail={t('dashboard.reportsApprovedHelp')}
        />
      </section>

      {/* `items-start`, so each column ends where its content does. The grid
          stretches both to the taller one by default, which left the list of
          people with a few hundred pixels of empty card under it whenever the
          panel beside it ran long. Forcing equal heights only looks like
          harmony when both sides happen to be the same length. */}
      <div className="mt-8 grid items-start gap-8 xl:grid-cols-[1fr_384px]">
        <section className="flex flex-col">
          <h2 className="mb-2.5 flex items-center gap-2 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
            {t('dashboard.byParticipant')}
            <span className="h-px flex-1 bg-line-soft" />
            <Link to="/participantes" className="text-teal hover:text-teal-deep">
              {t('dashboard.seeAll')} →
            </Link>
          </h2>
          <ParticipantTable participants={participants} />
        </section>

        <div className="flex flex-col gap-8">
          <ParticipationDonut
            completed={submittedByRelationship}
            invited={invitedByRelationship}
          />

          <section>
            <h2 className="mb-2.5 flex items-center gap-2 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
              {t('dashboard.alerts')}
              <span className="h-px flex-1 bg-line-soft" />
            </h2>
            <AlertList alerts={alerts} limit={3} />
          </section>
        </div>
      </div>

      <p className="mt-10 border-t border-line-soft pt-4 text-xs text-faint">
        {t('dashboard.privacyFooter')}
      </p>
    </div>
  );
};
